import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Modal, Popup, Checkbox } from "semantic-ui-react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { SG_SET_MEMBER_FILTERS } from "constants/actions";
import CloseImg from "assets/images/nav/close.svg";
import PlusImg from "assets/images/nav/plus.svg";
import ClearImg from "assets/images/nav/clear.svg";
import CheckImg from "assets/images/nav/check.svg";
import Button from "new_components/atoms/Button";

const ModernFilter = ({
    categories,
    setFilterCategories,
    setFilterPermissions,
    setMissingCategoriesFilter,
    resetMemberList,
}) => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [filteredPermissions, setLocalPermissions] = useState([]);
    const [missingCategories, setMissingCategories] = useState(false);
    const categoryRefs = useRef({});

    const memberFilters = useSelector(
        (state) => state.member_filters,
        shallowEqual
    );

    const ACCOUNT_PERMISSIONS = [
        { label: "Access Data Analytics", property: "access_data_analytics" },
        { label: "Participate in Survey", property: "survey_status" },
        { label: "Access Subfactor Data", property: "access_subfactor_data" },
        { label: "Can Read Feedback", property: "read_feedback" },
        { label: "Can Schedule Surveys", property: "survey_schedule" },
        { label: "Has a Login", property: "enable_login" },
        { label: "Is an Admin", property: "account_type" },
    ];

    useEffect(() => {
        if (memberFilters) {
            setSelectedFilters(memberFilters.filteredCategoriesId || []);
            setLocalPermissions(memberFilters.filteredPermissions || []);
            setMissingCategories(memberFilters.missingCategoriesFilter || false);
        }
    }, [memberFilters]);

    useEffect(() => {
        const refs = {};
        categories.forEach((category) => {
            refs[category.id] = React.createRef();
        });
        refs["permissions"] = React.createRef();
        categoryRefs.current = refs;
    }, [categories]);

    const toggleCategory = (id) => {
        const updated = selectedFilters.includes(id)
            ? selectedFilters.filter((i) => i !== id)
            : [...selectedFilters, id];
        setSelectedFilters(updated);
        updateFilters(updated, filteredPermissions, missingCategories);
    };

    const togglePermission = (perm) => {
        const updated = filteredPermissions.includes(perm)
            ? filteredPermissions.filter((p) => p !== perm)
            : [...filteredPermissions, perm];
        setLocalPermissions(updated);
        updateFilters(selectedFilters, updated, missingCategories);
    };

    const updateFilters = (updatedCategories, updatedPermissions, updatedMissingCategories) => {
        resetMemberList(); // Always start fresh
        dispatch({
            type: SG_SET_MEMBER_FILTERS,
            payload: {
                filteredCategories: updatedCategories,
                filteredCategoriesId: updatedCategories,
                filteredPermissions: updatedPermissions,
                missingCategoriesFilter: updatedMissingCategories,
            },
        });
        setFilterCategories([...updatedCategories]);
        setFilterPermissions([...updatedPermissions]);
        setMissingCategoriesFilter(updatedMissingCategories);
    };

    const clearAll = () => {
        setSelectedFilters([]);
        setLocalPermissions([]);
        setMissingCategories(false);
        resetMemberList();
        dispatch({
            type: SG_SET_MEMBER_FILTERS,
            payload: {
                filteredCategories: [],
                filteredCategoriesId: [],
                filteredPermissions: [],
                missingCategoriesFilter: false,
            },
        });
    };

    const applyFilters = () => {
        dispatch({
            type: SG_SET_MEMBER_FILTERS,
            payload: {
                filteredCategories: selectedFilters,
                filteredCategoriesId: selectedFilters,
                filteredPermissions,
                missingCategoriesFilter: missingCategories,
            },
        });
        setFilterCategories([...selectedFilters]);
        setFilterPermissions([...filteredPermissions]);
        setMissingCategoriesFilter(missingCategories);
        setOpen(false);
    };

    const getFilterName = (id) => {
        const category = categories.find((cat) => cat.options.find((opt) => opt.id === id));
        if (category) {
            const option = category.options.find((opt) => opt.id === id);
            return option.text || option.name;
        }
        return "";
    };

    const handleCategoryClick = (categoryId) => {
        categoryRefs.current[categoryId]?.current.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <Wrapper>
            <SelectedTags>
                {selectedFilters.map((id) => (
                    <Tag key={id} onClick={() => toggleCategory(id)}>
                        {getFilterName(id)} <img src={CloseImg} alt="Remove" />
                    </Tag>
                ))}
                {filteredPermissions.map((perm) => {
                    const label = ACCOUNT_PERMISSIONS.find((p) => p.property === perm)?.label || perm;
                    return (
                        <Tag key={perm} onClick={() => togglePermission(perm)}>
                            {label} <img src={CloseImg} alt="Remove" />
                        </Tag>
                    );
                })}
                {(selectedFilters.length > 0 || filteredPermissions.length > 0 || missingCategories) && (
                    <Popup
                        trigger={<ClearBtn src={ClearImg} onClick={clearAll} />}
                        content="Clear All"
                        inverted
                        position="top center"
                    />
                )}
            </SelectedTags>
            <Trigger onClick={() => setOpen(true)}>
                <img src={PlusImg} alt="+" /> Add Filters
            </Trigger>
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                closeIcon
                style={{ maxHeight: "90vh" }}
            >
                <Modal.Header>Member Filters</Modal.Header>
                <Modal.Content scrolling={false} style={{ padding: 0 }}>
                    <FilterLayout>
                        <Sidebar>
                            {categories.map((cat) => (
                                <SidebarItem key={cat.id} onClick={() => handleCategoryClick(cat.id)}>
                                    {cat.name}
                                </SidebarItem>
                            ))}
                            <SidebarItem onClick={() => handleCategoryClick("permissions")}>Permissions</SidebarItem>
                        </Sidebar>
                        <MainContent>
                            <ScrollableContent>
                                {categories.map((cat) => (
                                    <div key={cat.id} ref={categoryRefs.current[cat.id]}>
                                        <strong>{cat.name}</strong>
                                        <Options>
                                            {cat.options.map((opt) => (
                                                <Option
                                                    key={opt.id}
                                                    onClick={() => toggleCategory(opt.id)}
                                                    selected={selectedFilters.includes(opt.id)}
                                                >
                                                    {selectedFilters.includes(opt.id) && <img src={CheckImg} alt="✓" />} {opt.name}
                                                </Option>
                                            ))}
                                        </Options>
                                    </div>
                                ))}
                                <div ref={categoryRefs.current["permissions"]}>
                                    <h4 style={{ marginTop: 20 }}>By Permission</h4>
                                    {ACCOUNT_PERMISSIONS.map((perm) => (
                                        <Checkbox
                                            key={perm.property}
                                            checked={filteredPermissions.includes(perm.property)}
                                            label={perm.label}
                                            onClick={() => togglePermission(perm.property)}
                                            style={{ marginTop: 10, display: "block" }}
                                        />
                                    ))}
                                    <Checkbox
                                        label="Missing Categories"
                                        checked={missingCategories}
                                        onClick={() => setMissingCategories(!missingCategories)}
                                        style={{ marginTop: 10, display: "block" }}
                                    />
                                </div>
                            </ScrollableContent>
                        </MainContent>
                    </FilterLayout>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={applyFilters}>Apply</Button>
                </Modal.Actions>
            </Modal>
        </Wrapper>
    );
};

export default ModernFilter;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const SelectedTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  margin-bottom: 5px;
  align-items: center;
`;

const Tag = styled.div`
  background-color: #ebf1fd;
  color: #2d70e2;
  padding: 6px 12px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  font-size: 13px;
  font-family: "Inter", sans-serif;
  white-space: nowrap;
  img {
    margin-left: 6px;
    width: 12px;
    cursor: pointer;
  }
`;

const Trigger = styled.div`
  color: #2d70e2;
  cursor: pointer;
  font-size: 14px;
  font-family: Raleway;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 20px;
`;

const ClearBtn = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const FilterLayout = styled.div`
  display: flex;
  height: 60vh;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 200px;
  border-right: 1px solid #e5e5e5;
  padding: 10px;
  overflow-y: auto;
`;

const SidebarItem = styled.div`
  font-size: 14px;
  padding: 6px 12px;
  margin-bottom: 4px;
  cursor: pointer;
  &:hover {
    background-color: #f8f8f8;
  }
`;

const MainContent = styled.div`
  flex: 1;
  overflow: hidden;
`;

const ScrollableContent = styled.div`
  padding: 10px 20px;
  height: 100%;
  overflow-y: auto;
`;

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const Option = styled.div`
  background: ${(props) => (props.selected ? "#EBF1FD" : "#fff")};
  color: ${(props) => (props.selected ? "#2D70E2" : "#2A3039")};
  border: 1px solid #dcdcdc;
  border-radius: 20px;
  padding: 6px 12px;
  margin-right: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  img {
    width: 14px;
    margin-right: 4px;
  }
`;
